import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button, Card } from '@w7/ui'
import { bookmakerApi, bmErrorMessage, unwrapBmList } from '@/api/bookmaker'
import {
  bookmakerSubscribeChannels,
  oddsFromRateRow,
  subscribeMarketChannels,
} from '@/lib/marketSocket'
import { BmPage } from './BmPage'
import { buildClearRatesUpdateManyBody } from './clearRatesPayload'
import { applyMarketTextSave } from './marketTextSave'
import { buildRateButtonUpdateManyBody } from './rateButtonPayload'
import {
  isMarketAbandoned,
  isMarketSettled,
  isMarketTied,
  resultHorseSelectionId,
  resultMarketId,
  resultWinnerHorseId,
  shouldShowWinnerBanner,
  winnerNameFromRunners,
} from './resultVendorIds'
import { bookmakerResultSuccessMessage } from './bookmakerResultResponse'
import './markets-view.css'

/** Collapse React StrictMode double-mount into one in-flight request per key. */
const bmInflight = new Map<string, Promise<unknown>>()
function dedupeBmGet(key: string, fn: () => Promise<unknown>): Promise<unknown> {
  const existing = bmInflight.get(key)
  if (existing) return existing
  const p = fn().finally(() => {
    bmInflight.delete(key)
  })
  bmInflight.set(key, p)
  return p
}

type RateRow = { runNo: number; _id?: string }
type BothRateRow = { backButtonNo: number; _id?: string }

type Runner = {
  _id: string
  id?: string
  selectionId?: string
  name: string
  back: number | null
  lay: number | null
  status: boolean
  ratebuttonData?: number | null
  rateDiff?: number | null
  runs?: RateRow[]
  market?: string
}

type Market = {
  _id: string
  id?: string
  betfairId?: string
  name: string
  eventName?: string
  event?: string
  eventId?: string
  status?: string
  ballRunnig: boolean
  bookmakerMultiOdds: boolean
  bookmakerCommission: boolean
  rateOnOff: boolean
  gameValue?: string
  runners: Runner[]
  isResult?: boolean
  winner?: string
  winnerName?: string
  winnerArray?: unknown
  bookmakerLimit: number | string
  marketInputText: string
  placeBetCalcType?: number
  isJodiMarket?: boolean
  result?: string
}

type ConfirmKind = 'winner' | 'tie' | 'abandon' | null

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
}

function unwrapData(v: unknown): unknown {
  if (v == null) return null
  if (typeof v !== 'object' || Array.isArray(v)) return v
  const o = v as Record<string, unknown>
  if ('data' in o && o.data != null && typeof o.data === 'object') return o.data
  if ('value' in o && o.value != null && typeof o.value === 'object') return o.value
  return v
}

function toBool(v: unknown): boolean {
  return v === true || v === 'Enable' || v === 'true' || v === 1
}

function statusLabel(on: boolean): 'Enable' | 'Disable' {
  return on ? 'Enable' : 'Disable'
}

function normalizeRunner(h: Record<string, unknown>, runs: RateRow[]): Runner {
  const meta =
    h.metadata && typeof h.metadata === 'object' && !Array.isArray(h.metadata)
      ? (h.metadata as Record<string, unknown>)
      : {}
  const back = h.back ?? meta.back
  const lay = h.lay ?? meta.lay
  return {
    _id: String(h._id ?? h.id ?? h.selectionId ?? h.selection_id ?? ''),
    id:
      h.id != null
        ? String(h.id)
        : h.selectionId != null
          ? String(h.selectionId)
          : h.selection_id != null
            ? String(h.selection_id)
            : undefined,
    selectionId:
      h.selectionId != null
        ? String(h.selectionId)
        : h.selection_id != null
          ? String(h.selection_id)
          : undefined,
    name: String(h.name ?? h.runnerName ?? ''),
    back: back == null || back === '' ? null : Number(back),
    lay: lay == null || lay === '' ? null : Number(lay),
    status: toBool(h.status ?? meta.status),
    ratebuttonData:
      (h.ratebuttonData ?? meta.ratebuttonData) == null
        ? null
        : Number(h.ratebuttonData ?? meta.ratebuttonData),
    runs: Array.isArray(h.runs)
      ? (h.runs as RateRow[])
      : Array.isArray(meta.runs)
        ? (meta.runs as RateRow[])
        : runs,
    market: h.market != null ? String(h.market) : undefined,
  }
}

function normalizeMarket(raw: unknown, runs: RateRow[]): Market | null {
  const data = asRecord(unwrapData(raw))
  if (!data._id && !data.id && !data.betfairId) return null
  // Catalog odds live on `runners` only.
  const runnersRaw = Array.isArray(data.runners)
    ? data.runners
    : Array.isArray(data.horses)
      ? data.horses
      : []
  return {
    _id: String(data._id ?? data.id ?? ''),
    id: data.id != null ? String(data.id) : undefined,
    betfairId: data.betfairId != null ? String(data.betfairId) : undefined,
    name: String(data.name ?? data.marketName ?? ''),
    eventName: String(data.eventName ?? ''),
    event: data.event != null ? String(data.event) : undefined,
    eventId: data.eventId != null ? String(data.eventId) : data.event != null ? String(data.event) : undefined,
    status: data.status != null ? String(data.status) : undefined,
    // Lab typo `ballRunnig` + player spellings — getone may only set one.
    ballRunnig: toBool(data.ballRunnig ?? data.ballRunning ?? data.ballChalu),
    bookmakerMultiOdds: toBool(data.bookmakerMultiOdds),
    bookmakerCommission: toBool(data.bookmakerCommission),
    rateOnOff: toBool(data.rateOnOff),
    gameValue: data.gameValue != null ? String(data.gameValue) : '',
    runners: runnersRaw.map((h) => normalizeRunner(asRecord(h), runs)),
    isResult: Boolean(data.isResult),
    winner: data.winner != null ? String(data.winner) : undefined,
    winnerName: data.winnerName != null ? String(data.winnerName) : undefined,
    winnerArray: data.winnerArray,
    bookmakerLimit: (data.bookmakerLimit as number | string) ?? 0,
    marketInputText: String(data.marketInputText ?? ''),
    placeBetCalcType: data.placeBetCalcType != null ? Number(data.placeBetCalcType) : 0,
    isJodiMarket: Boolean(data.isJodiMarket),
    result: data.result != null ? String(data.result) : undefined,
  }
}

function Toggle({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean
  disabled?: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <label className="bm-mv-toggle">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="slider" />
    </label>
  )
}

export function MarketsViewPage() {
  const { id } = useParams()
  const [market, setMarket] = useState<Market | null>(null)
  const [runs, setRuns] = useState<RateRow[]>([])
  const [runsDiff, setRunsDiff] = useState<RateRow[]>([])
  const [bothBack, setBothBack] = useState<BothRateRow[]>([])
  const [rateButtonData, setRateButtonData] = useState<number | ''>('')
  const [rateDiffSelected, setRateDiffSelected] = useState<number | ''>(1)
  const [winnerName, setWinnerName] = useState('')
  const [winnerNames, setWinnerNames] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  const [resultOpen, setResultOpen] = useState(false)
  const [resultRunners, setResultRunners] = useState<Record<string, unknown>[]>([])
  const [marketResultId, setMarketResultId] = useState<string | null>(null)
  const [marketStatusSnap, setMarketStatusSnap] = useState<string | null>(null)
  const [resultCommission, setResultCommission] = useState(0)
  const [confirm, setConfirm] = useState<{
    kind: ConfirmKind
    runnerId?: string
    winners?: Record<string, unknown>[]
    withdraw?: Record<string, unknown>[]
  } | null>(null)
  const [hrWinners, setHrWinners] = useState<string[]>([])
  const [hrWithdraw, setHrWithdraw] = useState<string[]>([])

  const backDraft = useRef<Record<string, number | null>>({})
  const marketRef = useRef(market)
  marketRef.current = market
  const runsRef = useRef<RateRow[]>([])
  runsRef.current = runs
  /** Last back/lay successfully sent to horseEdit — blur must not no-op after onChange. */
  const committedOdds = useRef<Record<string, { back?: number | null; lay?: number | null }>>({})
  /** Bumped on every runner edit — stale getone / SSE must not overwrite newer local saves. */
  const editEpoch = useRef(0)
  const ignoreSseUntil = useRef(0)
  const editChain = useRef(Promise.resolve<unknown>(undefined))

  const stayDisabled = isMarketSettled(market ?? {}) || isMarketTied(market ?? {}) || isMarketAbandoned(market ?? {})

  const isHorseRacing = market?.gameValue === 'Horse Racing' || market?.placeBetCalcType === 2

  const loadMarket = useCallback(
    async (rateList?: RateRow[]) => {
      if (!id) return
      const epochAtStart = editEpoch.current
      setError(null)
      try {
        const raw = await bookmakerApi.marketGetOne({ id })
        // A Lay/Status save finished while getone was in flight — keep the save, drop this snapshot.
        if (epochAtStart !== editEpoch.current) return
        const next = normalizeMarket(raw, rateList ?? runsRef.current)
        if (!next) {
          setError('No market found')
          setMarket(null)
          return
        }
        if (
          next.status === 'Disable' ||
          isMarketTied(next) ||
          isMarketAbandoned(next) ||
          isMarketSettled(next)
        ) {
          next.ballRunnig = false
        }
        if (epochAtStart !== editEpoch.current) return
        // getone is source of truth — briefly ignore SSE rates so a stale
        // blast cannot repaint old H1/H2 over null/cleared catalog odds.
        ignoreSseUntil.current = Date.now() + 900
        setMarket(next)
        committedOdds.current = Object.fromEntries(
          next.runners.map((r) => [r._id, { back: r.back, lay: r.lay }]),
        )
        if (next.runners[0]?.ratebuttonData != null) {
          setRateButtonData(Number(next.runners[0].ratebuttonData))
        }
        if (isMarketSettled(next)) {
          const metaName = String(next.winnerName ?? '').trim()
          if (metaName) {
            setWinnerName(metaName)
          } else if (next.winner) {
            const fromRunners = winnerNameFromRunners(next.runners, next.winner)
            if (fromRunners) {
              setWinnerName(fromRunners)
            } else {
              try {
                const runner = asRecord(
                  unwrapData(await bookmakerApi.horseGetOne({ id: next.winner })),
                )
                setWinnerName(String(runner.name ?? runner.runnerName ?? ''))
              } catch {
                setWinnerName('')
              }
            }
          } else if (Array.isArray(next.winnerArray) && next.winnerArray.length) {
            try {
              const list = unwrapBmList(await bookmakerApi.horseGetMultiple(next.winnerArray))
              setWinnerNames(list.map((h) => String(h.name ?? '')).filter(Boolean))
            } catch {
              setWinnerNames([])
            }
          } else {
            setWinnerName('')
            setWinnerNames([])
          }
        } else {
          setWinnerName('')
          setWinnerNames([])
        }
      } catch (e) {
        setError(bmErrorMessage(e))
      }
    },
    [id],
  )

  useEffect(() => {
    if (!id) return
    let cancelled = false

    void (async () => {
      const [ratesRes, diffRes, bothRes] = await Promise.allSettled([
        dedupeBmGet('Rates/getAllRates', () => bookmakerApi.ratesGetAll()),
        dedupeBmGet('Ratesdiff/getAllRatesDiff', () => bookmakerApi.ratesDiffGetAll()),
        dedupeBmGet('BothRates/getAllRates', () => bookmakerApi.bothRatesGetAll()),
      ])
      if (cancelled) return

      let rateList: RateRow[] = []
      if (ratesRes.status === 'fulfilled') {
        rateList = unwrapBmList(ratesRes.value)
          .map((r) => ({
            runNo: Number(r.runNo ?? r.rate ?? r.value ?? NaN),
            _id: r._id != null ? String(r._id) : undefined,
          }))
          .filter((r) => Number.isFinite(r.runNo) && r.runNo > 0)
        setRuns(rateList)
        runsRef.current = rateList
      } else {
        setRuns([])
      }

      if (diffRes.status === 'fulfilled') {
        setRunsDiff(
          unwrapBmList(diffRes.value)
            .map((r) => ({
              runNo: Number(r.runNo ?? r.rateDiff ?? r.value ?? NaN),
              _id: r._id != null ? String(r._id) : undefined,
            }))
            .filter((r) => Number.isFinite(r.runNo) && r.runNo > 0),
        )
      } else {
        setRunsDiff([])
      }

      if (bothRes.status === 'fulfilled') {
        setBothBack(
          unwrapBmList(bothRes.value)
            .map((r) => ({
              backButtonNo: Number(r.backButtonNo ?? r.back ?? r.runNo ?? NaN),
              _id: r._id != null ? String(r._id) : undefined,
            }))
            .filter((r) => Number.isFinite(r.backButtonNo)),
        )
      } else {
        setBothBack([])
      }

      await loadMarket(rateList)
    })()

    return () => {
      cancelled = true
    }
  }, [id, loadMarket])

  // Live odds / trading flags via Socket-API SSE (same channels as players).
  // Join vendor `1.xxx#bookmaker{n}` — API blasts that id, not the route UUID.
  useEffect(() => {
    if (!id) return
    const vendorMarketId = String(market?.betfairId ?? market?.id ?? '').trim()
    const channels = bookmakerSubscribeChannels({ routeId: id, vendorMarketId })
    return subscribeMarketChannels(channels, (payload) => {
      // Echo of our own horseEdit / fresh getone — skip rates briefly so the
      // grid matches Market/getone (not a stale SSE ladder over null DB odds).
      if (Date.now() < ignoreSseUntil.current) {
        const src = String(payload.source ?? '')
        if (src === 'horseEdit' || src === 'updateManyHorse' || src === 'saveMarket') {
          // Still allow Ball Running / flag-only frames without rates.
          const rates = payload.rates
          if (Array.isArray(rates) && rates.length) return
          if (src === 'horseEdit' || src === 'updateManyHorse') return
        }
      }
      const rates = Array.isArray(payload.rates) ? (payload.rates as Record<string, unknown>[]) : null
      setMarket((m) => {
        if (!m) return m
        let runners = m.runners
        if (rates && rates.length) {
          const byId = new Map<string, Record<string, unknown>>()
          for (const row of rates) {
            const key = String(row.selection ?? row.id ?? row.betfairId ?? row._id ?? '')
            if (key) byId.set(key, row)
          }
          runners = m.runners.map((r) => {
            const hit =
              byId.get(r._id) ||
              byId.get(String(r.id ?? '')) ||
              byId.get(String(r.selectionId ?? '')) ||
              [...byId.values()].find(
                (row) =>
                  String(row.name ?? '')
                    .trim()
                    .toLowerCase() === String(r.name ?? '').trim().toLowerCase(),
              )
            if (!hit) return r
            const odds = oddsFromRateRow(hit)
            return {
              ...r,
              ...(odds.back !== undefined ? { back: odds.back } : {}),
              ...(odds.lay !== undefined ? { lay: odds.lay } : {}),
              ...(odds.status !== undefined ? { status: toBool(odds.status) } : {}),
              ...(odds.ratebuttonData !== undefined ? { ratebuttonData: odds.ratebuttonData } : {}),
              ...(odds.rateDiff !== undefined ? { rateDiff: odds.rateDiff } : {}),
            }
          })
        }
        // Odds-only horseEdit / updateManyHorse often re-sends ballRunnig:false
        // from a stale form — that must not flip Ball Running off in the grid.
        const src = String(payload.source ?? '')
        const ballRaw =
          payload.ballRunning ?? payload.ballChalu ?? payload.ballRunnig
        const ballOn = ballRaw === true || ballRaw === 'Enable' || ballRaw === 'true'
        const applyBall =
          ballRaw !== undefined &&
          (src === 'saveMarket' ||
            src === '' ||
            ballOn ||
            (src !== 'horseEdit' && src !== 'updateManyHorse'))
        const next = {
          ...m,
          runners,
          ...(applyBall ? { ballRunnig: ballOn } : {}),
          // Input text only from explicit text save — odds SSE often carried a stale banner.
          ...(payload.marketInputText !== undefined && src === 'saveMarket'
            ? { marketInputText: String(payload.marketInputText ?? '') }
            : {}),
          ...(payload.bookmakerMultiOdds !== undefined
            ? { bookmakerMultiOdds: Boolean(payload.bookmakerMultiOdds) }
            : {}),
          ...(payload.bookmakerCommission !== undefined
            ? { bookmakerCommission: Boolean(payload.bookmakerCommission) }
            : {}),
          ...(payload.rateOnOff !== undefined ? { rateOnOff: Boolean(payload.rateOnOff) } : {}),
          ...(payload.bookmakerLimit !== undefined && src === 'saveMarket'
            ? { bookmakerLimit: Number(payload.bookmakerLimit) }
            : {}),
        }
        const firstBtn = next.runners.find((r) => r.ratebuttonData != null)?.ratebuttonData
        if (firstBtn != null) {
          queueMicrotask(() => setRateButtonData(Number(firstBtn)))
        }
        return next
      })
    })
  }, [id, market?.betfairId, market?.id])


  /** Persist a runner odds/status edit; market payload always carries `runners`. */
  async function runnerEdit(runner: Runner, toUpdateData: Record<string, unknown>) {
    const run = async () => {
      const m = marketRef.current
      if (!m) return null
      const epoch = ++editEpoch.current
      ignoreSseUntil.current = Date.now() + 1200
      // Always take latest runners from ref — Toggle/Enter closures go stale mid-edit.
      const latest =
        m.runners.find((r) => r._id === runner._id || String(r.id) === String(runner.id)) ?? runner
      const mergedRunner: Runner = {
        ...latest,
        ...runner,
        back: (toUpdateData.back as number | null | undefined) !== undefined
          ? (toUpdateData.back as number | null)
          : (runner.back ?? latest.back),
        lay: (toUpdateData.lay as number | null | undefined) !== undefined
          ? (toUpdateData.lay as number | null)
          : (runner.lay ?? latest.lay),
        status:
          toUpdateData.status !== undefined
            ? toBool(toUpdateData.status)
            : (runner.status ?? latest.status),
      }
      const runners = m.runners.map((r) =>
        r._id === mergedRunner._id || String(r.id) === String(mergedRunner.id) ? mergedRunner : r,
      )
      // Keep ref in sync so the next queued edit sees this save (not pre-click state).
      marketRef.current = { ...m, runners }
      const marketPayload = {
        ...m,
        runners,
        horses: runners,
        betfairId: m.betfairId ?? m.id,
        id: m.betfairId ?? m.id,
      }
      const res = asRecord(
        await bookmakerApi.horseHorseEdit({
          horse: mergedRunner,
          runner: mergedRunner,
          market: marketPayload,
          toUpdateData,
        }),
      )
      if (epoch !== editEpoch.current) return null
      applySocketRatesToMarket(
        Array.isArray(res.rates) ? (res.rates as Record<string, unknown>[]) : null,
      )
      return res
    }
    const p = editChain.current.then(run, run)
    editChain.current = p.then(
      () => undefined,
      () => undefined,
    )
    return p
  }

  /** Apply horseEdit / SSE rate rows into the grid immediately (no getone round-trip). */
  function applySocketRatesToMarket(rates: Record<string, unknown>[] | null) {
    if (!rates?.length) return
    setMarket((m) => {
      if (!m) return m
      const byId = new Map<string, Record<string, unknown>>()
      for (const row of rates) {
        const key = String(row.selection ?? row.selectionId ?? row.id ?? row.betfairId ?? row._id ?? '')
        if (key) byId.set(key, row)
        const name = String(row.name ?? '')
          .trim()
          .toLowerCase()
        if (name) byId.set(`name:${name}`, row)
      }
      const runners = m.runners.map((r) => {
        const hit =
          byId.get(r._id) ||
          byId.get(String(r.id ?? '')) ||
          byId.get(String(r.selectionId ?? '')) ||
          byId.get(`name:${String(r.name ?? '').trim().toLowerCase()}`)
        if (!hit) return r
        const odds = oddsFromRateRow(hit)
        return {
          ...r,
          ...(odds.back !== undefined ? { back: odds.back } : {}),
          ...(odds.lay !== undefined ? { lay: odds.lay } : {}),
          ...(odds.status !== undefined ? { status: toBool(odds.status) } : {}),
          ...(odds.ratebuttonData !== undefined ? { ratebuttonData: odds.ratebuttonData } : {}),
          ...(odds.rateDiff !== undefined ? { rateDiff: odds.rateDiff } : {}),
        }
      })
      return { ...m, runners }
    })
  }

  async function patchMarket(patch: Record<string, unknown>) {
    const m = marketRef.current
    if (!m) return
    const betfairId = String(m.betfairId ?? m.id ?? '')
    const nextMarket = {
      ...m,
      ...patch,
      runners: m.runners,
      betfairId,
      id: betfairId,
    }
    await bookmakerApi.marketEdit({
      ...patch,
      _id: m._id,
      id: betfairId,
      betfairId,
      marketId: betfairId,
      name: m.name,
      eventId: m.eventId ?? m.event,
      event: m.event ?? m.eventId,
      market: nextMarket,
      runners: m.runners,
      horses: m.runners,
    })
  }

  function updateRunnerLocal(runnerId: string, patch: Partial<Runner>) {
    setMarket((m) => {
      if (!m) return m
      const next = {
        ...m,
        runners: m.runners.map((r) => (r._id === runnerId ? { ...r, ...patch } : r)),
      }
      // Sync ref immediately — Enter handlers must not read a stale render closure.
      marketRef.current = next
      return next
    })
  }

  function readOddsInput(el: EventTarget | null): number | null {
    if (!(el instanceof HTMLInputElement)) return null
    const raw = el.value.trim()
    if (raw === '') return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  }

  async function onClearRates() {
    if (!market) return
    setBusy(true)
    setError(null)
    try {
      // One updateManyHorse — parallel horseEdit races rewrite sibling runners
      // from stale client state and leave uncleared odds (also blasted to Frontend).
      await bookmakerApi.horseUpdateMany(buildClearRatesUpdateManyBody(market))
      setRateButtonData('')
      setRateDiffSelected('')
      bmInflight.delete(`Market/getone:${id}`)
      await loadMarket()
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  async function onRateClick(rateData: number) {
    if (!market || !market.rateOnOff) return
    setBusy(true)
    setError(null)
    try {
      // One updateManyHorse — parallel horseEdit races rewrite siblings + blast bad rates.
      await bookmakerApi.horseUpdateMany(
        buildRateButtonUpdateManyBody(market, rateData, backDraft.current),
      )
      setRateButtonData(rateData)
      bmInflight.delete(`Market/getone:${id}`)
      await loadMarket()
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  async function onRateDiffClick(rateDiff: number) {
    if (!market) return
    setBusy(true)
    setError(null)
    try {
      const betfairId = String(market.betfairId ?? market.id ?? '')
      await bookmakerApi.horseUpdateMany({
        marketId: market._id,
        _id: market._id,
        id: betfairId,
        betfairId,
        rateDiff,
      })
      setRateDiffSelected(rateDiff)
      bmInflight.delete(`Market/getone:${id}`)
      await loadMarket()
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  async function onBothBack(back: number) {
    if (!market) return
    setBusy(true)
    setError(null)
    try {
      const betfairId = String(market.betfairId ?? market.id ?? '')
      await bookmakerApi.horseUpdateMany({
        marketId: market._id,
        _id: market._id,
        id: betfairId,
        betfairId,
        back,
        lay: null,
      })
      bmInflight.delete(`Market/getone:${id}`)
      await loadMarket()
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  async function commitBack(runner: Runner, backData: number | null) {
    if (!marketRef.current) return
    const status = statusLabel(runner.status)
    if ((runner.ratebuttonData != null || rateButtonData !== '') && marketRef.current.rateOnOff) {
      const rate = Number(runner.ratebuttonData ?? rateButtonData)
      await runnerEdit(runner, {
        back: backData,
        lay: backData == null ? null : Number((rate + Number(backData)).toFixed(2)),
        status,
      })
    } else if (backData == null || runner.lay == null || Number(backData) <= Number(runner.lay)) {
      await runnerEdit(runner, { back: backData, lay: runner.lay, status })
    } else {
      window.alert('Please Enter Back Rate Equal Or Less Than Lay Rate')
      updateRunnerLocal(runner._id, { back: null })
      return
    }
    backDraft.current[runner._id] = null
    committedOdds.current[runner._id] = {
      ...committedOdds.current[runner._id],
      back: backData,
    }
  }

  async function commitLay(runner: Runner, layData: number | null) {
    if (!marketRef.current) return
    const latest =
      marketRef.current.runners.find((r) => r._id === runner._id) ?? runner
    const status = statusLabel(latest.status)
    if (layData != null && latest.back != null && Number(latest.back) > Number(layData)) {
      window.alert('Please Enter Lay Rate Equal Or Greater Than Back Rate')
      updateRunnerLocal(runner._id, { lay: null })
      return
    }
    // Optimistic local lay so FE-facing blast + grid stay in sync if getone is slow.
    updateRunnerLocal(runner._id, { lay: layData })
    await runnerEdit({ ...latest, lay: layData }, { back: latest.back, lay: layData, status })
    committedOdds.current[runner._id] = {
      ...committedOdds.current[runner._id],
      lay: layData,
    }
  }

  async function openResults() {
    if (!market) return
    setBusy(true)
    setError(null)
    try {
      // Prefer in-memory runners (already from getOne); optional Horse/getbyid if API returns list.
      const fromApi = unwrapBmList(
        await bookmakerApi.horseGetById({
          id: market._id,
          name: market.name,
          betfairId: market.betfairId ?? market.id,
          eventName: market.eventName,
        }),
      )
      const local = market.runners.map((r) => ({ ...r }))
      // Prefer API list when it has usable names; otherwise keep in-memory runners.
      const apiHasNames = fromApi.some((r) => String(r.name ?? '').trim())
      setResultRunners(apiHasNames ? fromApi : local)
      setMarketResultId(market.result ?? null)
      setMarketStatusSnap(market.status ?? null)
      setHrWinners([])
      setHrWithdraw([])
      setResultCommission(0)
      setResultOpen(true)
    } catch {
      setResultRunners(market.runners.map((r) => ({ ...r })))
      setMarketResultId(market.result ?? null)
      setMarketStatusSnap(market.status ?? null)
      setResultOpen(true)
    } finally {
      setBusy(false)
    }
  }

  async function saveResult(kind: ConfirmKind, runnerId?: string) {
    if (!market || !kind) return
    setBusy(true)
    setError(null)
    try {
      const marketStatus = kind === 'winner' ? 'CLOSED' : kind === 'tie' ? 'TIED' : 'ABANDONED'
      if (isHorseRacing) {
        const winners =
          confirm?.winners ??
          resultRunners.filter((h) => hrWinners.includes(String(h._id ?? h.id)))
        const withdraw =
          confirm?.withdraw ??
          resultRunners.filter((h) => hrWithdraw.includes(String(h._id ?? h.id)))
        const res = await bookmakerApi.marketResultHorse({
          marketId: resultMarketId(market),
          horseBetfairIdArray: winners.map((h) => resultHorseSelectionId(h)),
          getHorseWithdrawData: withdraw,
          marketStatus,
          resultCommision: resultCommission,
          name: market.name,
          subGame: 'Horse Racing',
          betfairId: market.betfairId ?? market.id,
          eventName: market.eventName,
        })
        setConfirm(null)
        setResultOpen(false)
        await loadMarket()
        setMsg(bookmakerResultSuccessMessage(res))
      } else {
        const res = await bookmakerApi.marketResult({
          marketId: resultMarketId(market),
          horseId: resultWinnerHorseId(market.runners, runnerId),
          marketStatus,
          resultCommision: resultCommission,
        })
        setConfirm(null)
        setResultOpen(false)
        await loadMarket()
        setMsg(bookmakerResultSuccessMessage(res))
      }
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const m = marketRef.current
      if (!m) return
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      const key = e.key
      if (key === 'c' || key === 'C') {
        e.preventDefault()
        void onClearRates()
      } else if (key === 'b' || key === 'B') {
        e.preventDefault()
        const next = !m.ballRunnig
        setMarket({ ...m, ballRunnig: next })
        void patchMarket({ ballRunnig: next })
          .then(() => loadMarket())
          .catch((err) => setError(bmErrorMessage(err)))
      } else if (key === 'm' || key === 'M') {
        e.preventDefault()
        const next = !m.bookmakerMultiOdds
        setMarket({ ...m, bookmakerMultiOdds: next })
        void patchMarket({ bookmakerMultiOdds: next })
          .then(() => loadMarket())
          .catch((err) => setError(bmErrorMessage(err)))
      } else if (key === 'a' || key === 'A') {
        document.getElementById('focus1-0')?.focus()
      } else if (key === 's' || key === 'S') {
        document.getElementById('focus1-1')?.focus()
      } else if (key === 'd' || key === 'D') {
        document.getElementById('focus1-2')?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  return (
    <BmPage
      title="Markets View"
      description={market?.eventName || market?.name || `Lab /marketsview/${id ?? ''}`}
      error={error}
      actions={
        <Button disabled={busy} onClick={() => void loadMarket()}>
          Refresh
        </Button>
      }
    >
      {!market ? (
        <Card>
          <p className="bm-mv-empty">{busy ? 'Loading…' : 'No market loaded.'}</p>
        </Card>
      ) : (
        <div className="bm-mv">
          {msg ? <p style={{ margin: 0, color: '#1b7a3d' }}>{msg}</p> : null}

          {shouldShowWinnerBanner(market) ? (
            <div className="bm-mv-banner winner">
              {(() => {
                const label =
                  winnerName ||
                  market.winnerName ||
                  (winnerNames.length ? winnerNames.join(', ') : '') ||
                  winnerNameFromRunners(market.runners, market.winner)
                return label
                  ? `Winner Declared: ${label} won the match`
                  : 'Winner Declared'
              })()}
            </div>
          ) : null}
          {isMarketTied(market) ? <div className="bm-mv-banner tied">Market Tied!!!</div> : null}
          {isMarketAbandoned(market) ? (
            <div className="bm-mv-banner abandoned">Market Abandoned!!!</div>
          ) : null}

          <h1 className="bm-mv-title">{market.eventName || market.name}</h1>

          <Card>
            <div className="bm-mv-toolbar">
              <h2>{market.name}</h2>
              <div className="bm-mv-toolbar-actions">
                <Button className="bm-mv-btn-danger" disabled={busy} onClick={() => void onClearRates()}>
                  Clear Rates
                </Button>
                {!isMarketSettled(market) ? (
                  <Button className="bm-mv-btn-success" disabled={busy} onClick={() => void openResults()}>
                    Results
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="bm-mv-switches" style={{ marginTop: 16 }}>
              <div className="bm-mv-switch">
                <label className="name">Ball Running</label>
                <Toggle
                  checked={market.ballRunnig}
                  disabled={stayDisabled || busy}
                  onChange={(next) => {
                    setMarket({ ...market, ballRunnig: next })
                    void patchMarket({ ballRunnig: next })
                      .then(() => loadMarket())
                      .catch((e) => setError(bmErrorMessage(e)))
                  }}
                />
                <span className={market.ballRunnig ? 'state on' : 'state off'}>
                  {market.ballRunnig ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="bm-mv-switch">
                <label className="name">Multiple Odds</label>
                <Toggle
                  checked={market.bookmakerMultiOdds}
                  disabled={stayDisabled || busy}
                  onChange={(next) => {
                    setMarket({ ...market, bookmakerMultiOdds: next })
                    void patchMarket({ bookmakerMultiOdds: next })
                      .then(() => loadMarket())
                      .catch((e) => setError(bmErrorMessage(e)))
                  }}
                />
                <span className={market.bookmakerMultiOdds ? 'state on' : 'state off'}>
                  {market.bookmakerMultiOdds ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="bm-mv-switch">
                <label className="name">Commission</label>
                <Toggle
                  checked={market.bookmakerCommission}
                  disabled={stayDisabled || busy}
                  onChange={(next) => {
                    setMarket({ ...market, bookmakerCommission: next })
                    void patchMarket({ bookmakerCommission: next })
                      .then(() => loadMarket())
                      .catch((e) => setError(bmErrorMessage(e)))
                  }}
                />
                <span className={market.bookmakerCommission ? 'state on' : 'state off'}>
                  {market.bookmakerCommission ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>

            <div className="bm-mv-tables" style={{ marginTop: 16 }}>
              <table className="bm-mv-table">
                <thead>
                  <tr>
                    <th>Horse Name</th>
                    <th className="center">Back</th>
                    <th className="center">Lay</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {market.runners.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="bm-mv-empty">
                        No runners found for this market
                      </td>
                    </tr>
                  ) : (
                    market.runners.map((runner, index) => (
                      <tr key={runner._id || index}>
                        <td>
                          <div style={{ textTransform: 'capitalize' }}>{runner.name}</div>
                        </td>
                        <td className="center">
                          <input
                            id={`focus1-${index}`}
                            className="bm-mv-back"
                            type="number"
                            min={0}
                            max={99999}
                            step={0.01}
                            value={runner.back ?? ''}
                            disabled={busy}
                            onChange={(e) => {
                              const v = e.target.value === '' ? null : Number(e.target.value)
                              backDraft.current[runner._id] = v
                              updateRunnerLocal(runner._id, { back: v })
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                const v = readOddsInput(e.target)
                                updateRunnerLocal(runner._id, { back: v })
                                backDraft.current[runner._id] = v
                                void commitBack(runner, v).catch((err) =>
                                  setError(bmErrorMessage(err)),
                                )
                              } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                e.preventDefault()
                                const cur = Number(readOddsInput(e.target) ?? 0)
                                const next =
                                  e.key === 'ArrowUp'
                                    ? Number((cur + 0.5).toFixed(2))
                                    : Math.max(0, Number((cur - 0.5).toFixed(2)))
                                backDraft.current[runner._id] = next
                                updateRunnerLocal(runner._id, { back: next })
                              }
                            }}
                            onBlur={(e) => {
                              const v = readOddsInput(e.target)
                              const prev = committedOdds.current[runner._id]?.back
                              const baseline =
                                prev !== undefined
                                  ? prev
                                  : (marketRef.current?.runners.find((r) => r._id === runner._id) ??
                                      runner
                                    ).back
                              if (v === baseline) return
                              updateRunnerLocal(runner._id, { back: v })
                              void commitBack(runner, v).catch((err) => setError(bmErrorMessage(err)))
                            }}
                          />
                        </td>
                        <td className="center">
                          <input
                            id={`focus2-${index}`}
                            className="bm-mv-lay"
                            type="number"
                            min={0}
                            max={99999}
                            step={0.01}
                            value={runner.lay ?? ''}
                            disabled={busy || (market.rateOnOff && !isHorseRacing)}
                            onChange={(e) => {
                              const v = e.target.value === '' ? null : Number(e.target.value)
                              updateRunnerLocal(runner._id, { lay: v })
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                const v = readOddsInput(e.target)
                                updateRunnerLocal(runner._id, { lay: v })
                                void commitLay(runner, v).catch((err) =>
                                  setError(bmErrorMessage(err)),
                                )
                              }
                            }}
                            onBlur={(e) => {
                              const v = readOddsInput(e.target)
                              const prev = committedOdds.current[runner._id]?.lay
                              const baseline =
                                prev !== undefined
                                  ? prev
                                  : (marketRef.current?.runners.find((r) => r._id === runner._id) ??
                                      runner
                                    ).lay
                              if (v === baseline) return
                              updateRunnerLocal(runner._id, { lay: v })
                              void commitLay(runner, v).catch((err) => setError(bmErrorMessage(err)))
                            }}
                          />
                        </td>
                        <td>
                          <div className="bm-mv-switch">
                            <Toggle
                              checked={runner.status}
                              disabled={stayDisabled || busy}
                              onChange={(next) => {
                                const m = marketRef.current
                                const latest =
                                  m?.runners.find((r) => r._id === runner._id) ?? runner
                                updateRunnerLocal(runner._id, { status: next })
                                void runnerEdit(
                                  { ...latest, status: next },
                                  {
                                    back: latest.back,
                                    lay: latest.lay,
                                    status: statusLabel(next),
                                  },
                                ).catch((err) => setError(bmErrorMessage(err)))
                              }}
                            />
                            <span className={runner.status ? 'state on' : 'state off'}>
                              {runner.status ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              <table className="bm-mv-table">
                <thead>
                  <tr>
                    <th>
                      <div className="bm-mv-toolbar" style={{ gap: 8 }}>
                        <span>Rates</span>
                        <div className="bm-mv-switch">
                          <Toggle
                            checked={market.rateOnOff}
                            disabled={busy}
                            onChange={(next) => {
                              setMarket({ ...market, rateOnOff: next })
                              void patchMarket({ rateOnOff: next })
                                .then(() => loadMarket())
                                .catch((e) => setError(bmErrorMessage(e)))
                            }}
                          />
                          <span className={market.rateOnOff ? 'state on' : 'state off'}>
                            {market.rateOnOff ? 'On' : 'Off'}
                          </span>
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {isHorseRacing ? (
                        <span className="bm-mv-empty">—</span>
                      ) : runs.length === 0 ? (
                        <div className="bm-mv-empty">No Rates Found</div>
                      ) : (
                        <div className="bm-mv-run-btns">
                          {runs.map((item) => (
                            <button
                              key={item._id ?? String(item.runNo)}
                              type="button"
                              className={rateButtonData === item.runNo ? 'active' : undefined}
                              disabled={!market.rateOnOff || busy}
                              onClick={() => void onRateClick(item.runNo)}
                            >
                              {String(item.runNo)}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                  {!isHorseRacing && runsDiff.length > 0 ? (
                    <tr>
                      <td>
                        <div className="bm-mv-both">
                          <span>Rates Diff</span>
                          <div className="bm-mv-run-btns">
                            {runsDiff.map((item) => (
                              <button
                                key={item._id ?? String(item.runNo)}
                                type="button"
                                className={rateDiffSelected === item.runNo ? 'active' : undefined}
                                disabled={busy || !market.bookmakerMultiOdds}
                                title={
                                  market.bookmakerMultiOdds
                                    ? undefined
                                    : 'Enable Multiple Odds to apply rate diff'
                                }
                                onClick={() => void onRateDiffClick(item.runNo)}
                              >
                                {String(item.runNo)}
                              </button>
                            ))}
                          </div>
                        </div>
                        {!market.bookmakerMultiOdds ? (
                          <p className="bm-mv-rates-hint">
                            Enable Multiple Odds to apply Rates Diff
                          </p>
                        ) : null}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            {!isHorseRacing && bothBack.length > 0 ? (
              <div className="bm-mv-both" style={{ marginTop: 12 }}>
                <span>Both Back Button:</span>
                <div className="bm-mv-run-btns">
                  {bothBack.map((item) => (
                    <button
                      key={item._id ?? String(item.backButtonNo)}
                      type="button"
                      disabled={busy}
                      onClick={() => void onBothBack(item.backButtonNo)}
                    >
                      {String(item.backButtonNo)}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="bm-mv-inputs" style={{ marginTop: 16 }}>
              <div className="bm-mv-field">
                <label>Suspend Limit</label>
                <input
                  type="number"
                  min={0}
                  value={market.bookmakerLimit}
                  onChange={(e) =>
                    setMarket({
                      ...market,
                      bookmakerLimit: e.target.value === '' ? '' : Number(e.target.value),
                    })
                  }
                />
              </div>
              <div className="bm-mv-field">
                <label>Input text</label>
                <input
                  type="text"
                  value={market.marketInputText}
                  onChange={(e) => setMarket({ ...market, marketInputText: e.target.value })}
                />
                <Button
                  disabled={busy}
                  onClick={() => {
                    void (async () => {
                      setBusy(true)
                      setError(null)
                      try {
                        const inputText = String(market.marketInputText ?? '')
                        const limit = market.bookmakerLimit
                        await bookmakerApi.marketUpdateOne({
                          _id: market._id,
                          id: market.betfairId ?? market.id,
                          betfairId: market.betfairId ?? market.id,
                          marketId: market.betfairId ?? market.id,
                          name: market.name,
                          eventId: market.eventId ?? market.event,
                          marketInputText: inputText,
                          bookmakerLimit: limit,
                        })
                        // Local apply only — getone/SSE used to restore the old banner after clear.
                        const next = applyMarketTextSave(marketRef.current ?? market, {
                          marketInputText: inputText,
                          bookmakerLimit: limit,
                        })
                        marketRef.current = next
                        setMarket(next)
                        setMsg('Text saved successfully')
                      } catch (e) {
                        setError(bmErrorMessage(e))
                      } finally {
                        setBusy(false)
                      }
                    })()
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {resultOpen && market ? (
        <div className="bm-mv-modal-backdrop" role="presentation" onClick={() => setResultOpen(false)}>
          <div className="bm-mv-modal" role="dialog" aria-modal onClick={(e) => e.stopPropagation()}>
            <div className="bm-mv-modal-header">
              <h3>
                Results For <b style={{ textTransform: 'capitalize' }}>{market.name}</b>
              </h3>
              <button type="button" aria-label="Close" onClick={() => setResultOpen(false)}>
                ×
              </button>
            </div>
            <div className="bm-mv-modal-body">
              <div className="bm-mv-field">
                <label htmlFor="bm-result-deduction">Deduction(%):-</label>
                <input
                  id="bm-result-deduction"
                  type="number"
                  value={resultCommission}
                  onChange={(e) => setResultCommission(Number(e.target.value) || 0)}
                />
              </div>

              {!isHorseRacing ? (
                <>
                  <table className="bm-mv-result-table">
                    <thead>
                      <tr>
                        <th scope="col">Horse</th>
                        <th scope="col">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultRunners.map((runner) => {
                        const rid = String(runner._id ?? runner.id ?? '')
                        const isWinner = marketResultId === rid
                        const horseName = String(runner.name ?? runner.runnerName ?? '').trim() || '—'
                        return (
                          <tr key={rid || horseName}>
                            <td>{horseName}</td>
                            <td>
                              <Button
                                size="sm"
                                variant={isWinner ? 'primary' : 'secondary'}
                                disabled={busy}
                                onClick={() => setConfirm({ kind: 'winner', runnerId: rid })}
                              >
                                {isWinner ? 'Is Winner' : 'Set As winner'}
                              </Button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                  <div className="bm-mv-modal-actions">
                    <Button disabled={busy} onClick={() => setConfirm({ kind: 'tie' })}>
                      {marketStatusSnap === 'TIED' ? 'Is Declared Tie' : 'Tie'}
                    </Button>
                    <Button disabled={busy} onClick={() => setConfirm({ kind: 'abandon' })}>
                      {marketStatusSnap === 'ABANDONED' ? 'Is Declared Abandoned' : 'Abandoned'}
                    </Button>
                    <Button onClick={() => setResultOpen(false)}>Cancel</Button>
                  </div>
                </>
              ) : (
                <>
                  <label>Select Winner Horse</label>
                  <select
                    multiple
                    style={{ minHeight: 100, width: '100%' }}
                    value={hrWinners}
                    onChange={(e) =>
                      setHrWinners(Array.from(e.target.selectedOptions).map((o) => o.value))
                    }
                  >
                    {resultRunners.map((h) => {
                      const rid = String(h._id ?? h.id ?? '')
                      return (
                        <option key={rid} value={rid}>
                          {String(h.name ?? '')}
                        </option>
                      )
                    })}
                  </select>
                  <label>Select Withdraw Horse</label>
                  <select
                    multiple
                    style={{ minHeight: 100, width: '100%' }}
                    value={hrWithdraw}
                    onChange={(e) =>
                      setHrWithdraw(Array.from(e.target.selectedOptions).map((o) => o.value))
                    }
                  >
                    {resultRunners.map((h) => {
                      const rid = String(h._id ?? h.id ?? '')
                      return (
                        <option key={rid} value={rid}>
                          {String(h.name ?? '')}
                        </option>
                      )
                    })}
                  </select>
                  <div className="bm-mv-modal-actions">
                    <Button
                      disabled={busy}
                      onClick={() => {
                        if (!hrWinners.length) {
                          window.alert('Please Select Winner Horse')
                          return
                        }
                        setConfirm({
                          kind: 'winner',
                          winners: resultRunners.filter((h) =>
                            hrWinners.includes(String(h._id ?? h.id)),
                          ),
                          withdraw: resultRunners.filter((h) =>
                            hrWithdraw.includes(String(h._id ?? h.id)),
                          ),
                        })
                      }}
                    >
                      Submit
                    </Button>
                    <Button disabled={busy} onClick={() => setConfirm({ kind: 'abandon' })}>
                      Abandoned
                    </Button>
                    <Button onClick={() => setResultOpen(false)}>Cancel</Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {confirm?.kind ? (
        <div className="bm-mv-modal-backdrop" role="presentation">
          <div className="bm-mv-modal" role="dialog" aria-modal>
            <div className="bm-mv-modal-header danger">
              <h3>Confirmation</h3>
            </div>
            <div className="bm-mv-modal-body">
              <p style={{ margin: 0 }}>
                {confirm.kind === 'tie'
                  ? `Are you sure you want to tie the market "${market?.name}"`
                  : confirm.kind === 'abandon'
                    ? `Are you sure you want to abandon the market "${market?.name}"`
                    : 'Are you sure you want to set selected horse as winner'}
              </p>
              <div className="bm-mv-modal-actions">
                <Button onClick={() => setConfirm(null)}>Cancel</Button>
                <Button
                  disabled={busy}
                  onClick={() => void saveResult(confirm.kind, confirm.runnerId)}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </BmPage>
  )
}
